const MIN_GAIN = 0.0001;
const MAX_SFX_VOICES = 18;
const DEFAULT_STORAGE_KEY = "game-audio-muted";

type AudioContextConstructor = new (options?: AudioContextOptions) => AudioContext;
type SourceNode = OscillatorNode | AudioBufferSourceNode;

export type ReceiverHitQuality = "safe" | "clean" | "perfect" | number;

export interface GameAudioOptions {
  storageKey?: string;
  initialMuted?: boolean;
  masterVolume?: number;
  musicVolume?: number;
  sfxVolume?: number;
  autoUnlock?: boolean;
  onMutedChange?: (muted: boolean) => void;
}

interface Voice {
  readonly sources: SourceNode[];
  readonly gain: GainNode;
  readonly startedAt: number;
  readonly endsAt: number;
  stopped: boolean;
}

interface AmbienceNodes {
  readonly droneA: OscillatorNode;
  readonly droneB: OscillatorNode;
  readonly air: AudioBufferSourceNode;
  readonly filter: BiquadFilterNode;
  readonly gain: GainNode;
  readonly pulse: OscillatorNode;
  readonly pulseDepth: GainNode;
}

interface FocusNodes {
  readonly oscillator: OscillatorNode;
  readonly filter: BiquadFilterNode;
  readonly gain: GainNode;
  stopped: boolean;
}

interface ToneOptions {
  duration: number;
  gain: number;
  type?: OscillatorType;
  attack?: number;
  delay?: number;
  endFrequency?: number;
  filterFrequency?: number;
  pan?: number;
}

function clamp(value: number, minimum = 0, maximum = 1): number {
  return Math.min(maximum, Math.max(minimum, value));
}

function getAudioContextConstructor(): AudioContextConstructor | undefined {
  if (typeof window === "undefined") return undefined;

  const browserWindow = window as typeof window & {
    webkitAudioContext?: AudioContextConstructor;
  };

  return browserWindow.AudioContext ?? browserWindow.webkitAudioContext;
}

function qualityAmount(quality: ReceiverHitQuality): number {
  if (typeof quality === "number") return clamp(quality);
  if (quality === "perfect") return 1;
  if (quality === "clean") return 0.68;
  return 0.32;
}

/**
 * Small, asset-free audio engine. Construction is silent: the AudioContext is
 * created only from the first pointer/touch/key gesture or an explicit unlock().
 */
export class GameAudio {
  private readonly storageKey: string;
  private readonly onMutedChange?: (muted: boolean) => void;
  private readonly autoUnlock: boolean;

  private context?: AudioContext;
  private masterBus?: GainNode;
  private musicBus?: GainNode;
  private sfxBus?: GainNode;
  private noiseBuffer?: AudioBuffer;
  private ambience?: AmbienceNodes;
  private focus?: FocusNodes;

  private readonly voices: Voice[] = [];
  private contextUnavailable = false;
  private destroyed = false;
  private pausedByUser = false;
  private pausedByVisibility = false;
  private overdriveActive = false;
  private pauseTimer?: number;

  private mutedValue: boolean;
  private masterVolume: number;
  private musicVolume: number;
  private sfxVolume: number;

  public constructor(options: GameAudioOptions = {}) {
    this.storageKey = options.storageKey ?? DEFAULT_STORAGE_KEY;
    this.onMutedChange = options.onMutedChange;
    this.autoUnlock = options.autoUnlock ?? true;
    this.masterVolume = clamp(options.masterVolume ?? 0.78);
    this.musicVolume = clamp(options.musicVolume ?? 0.42);
    this.sfxVolume = clamp(options.sfxVolume ?? 0.82);
    this.mutedValue =
      options.initialMuted ?? this.readPersistedMutedState() ?? false;

    if (this.autoUnlock) this.attachLifecycleListeners();
  }

  public get supported(): boolean {
    return !this.contextUnavailable && getAudioContextConstructor() !== undefined;
  }

  public get unlocked(): boolean {
    return this.context !== undefined;
  }

  public get muted(): boolean {
    return this.mutedValue;
  }

  public get contextState(): AudioContextState | "interrupted" | "unavailable" {
    if (!this.context) return "unavailable";
    return this.context.state as AudioContextState | "interrupted";
  }

  /** Call directly from a pointer handler when autoUnlock is disabled. */
  public async unlock(): Promise<boolean> {
    if (this.destroyed || this.contextUnavailable) return false;

    const context = this.context ?? this.createContext();
    if (!context) return false;
    if (this.pausedByUser || this.pausedByVisibility) return true;

    return this.resumeContext(context);
  }

  public setMuted(muted: boolean, persist = true): void {
    const didChange = muted !== this.mutedValue;
    this.mutedValue = muted;

    if (persist) this.persistMutedState(muted);
    this.applyBusLevels(0.035);
    if (didChange) this.onMutedChange?.(muted);
  }

  public toggleMuted(persist = true): boolean {
    this.setMuted(!this.mutedValue, persist);
    return this.mutedValue;
  }

  public setMasterVolume(volume: number): void {
    this.masterVolume = clamp(volume);
    this.applyBusLevels(0.04);
  }

  public setMusicVolume(volume: number): void {
    this.musicVolume = clamp(volume);
    this.applyBusLevels(0.04);
  }

  public setSfxVolume(volume: number): void {
    this.sfxVolume = clamp(volume);
    this.applyBusLevels(0.04);
  }

  public focusStart(intensity = 0.35): void {
    if (this.focus) {
      this.focusUpdate(intensity);
      return;
    }

    const graph = this.getSfxGraph();
    if (!graph) return;

    this.ensureVoiceCapacity(1);
    const { context, bus } = graph;
    const now = context.currentTime;
    const oscillator = context.createOscillator();
    const filter = context.createBiquadFilter();
    const gain = context.createGain();

    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(150 + clamp(intensity) * 170, now);
    filter.type = "bandpass";
    filter.Q.setValueAtTime(1.15, now);
    filter.frequency.setValueAtTime(480 + clamp(intensity) * 520, now);
    gain.gain.setValueAtTime(MIN_GAIN, now);
    gain.gain.exponentialRampToValueAtTime(0.036, now + 0.055);

    oscillator.connect(filter);
    filter.connect(gain);
    gain.connect(bus);
    oscillator.start(now);

    this.focus = { oscillator, filter, gain, stopped: false };
  }

  public focusUpdate(intensity: number): void {
    if (!this.context || !this.focus || this.focus.stopped) return;

    const amount = clamp(intensity);
    const now = this.context.currentTime;
    this.setTarget(this.focus.oscillator.frequency, 155 + amount * 310, now, 0.025);
    this.setTarget(this.focus.filter.frequency, 460 + amount * 980, now, 0.035);
    this.setTarget(this.focus.gain.gain, 0.027 + amount * 0.021, now, 0.025);
  }

  public focusEnd(cancelled = false): void {
    if (!this.context || !this.focus || this.focus.stopped) return;

    const focus = this.focus;
    this.focus = undefined;
    focus.stopped = true;
    const now = this.context.currentTime;
    const release = cancelled ? 0.028 : 0.055;
    this.fadeToSilence(focus.gain.gain, now, release);

    try {
      focus.oscillator.stop(now + release + 0.018);
    } catch {
      // A source may already have been stopped by an iOS interruption.
    }
  }

  public launch(strength = 0.7, pan = 0): void {
    const amount = clamp(strength);
    this.tone(145 + amount * 55, {
      duration: 0.24,
      gain: 0.085,
      type: "triangle",
      attack: 0.008,
      endFrequency: 480 + amount * 260,
      filterFrequency: 1550,
      pan,
    });
    this.noise(0.075, 0.023 + amount * 0.012, 1700, "highpass", pan);
  }

  public mirrorBounce(combo = 0, pan = 0): void {
    const safeCombo = Math.max(0, Math.floor(combo));
    const scale = [0, 2, 4, 7, 9, 12] as const;
    const scaleStep = scale[safeCombo % scale.length] ?? 0;
    const octave = Math.min(12, Math.floor(safeCombo / scale.length) * 5);
    const frequency = Math.min(1320, 330 * 2 ** ((scaleStep + octave) / 12));

    this.tone(frequency, {
      duration: 0.16,
      gain: 0.061,
      type: "sine",
      attack: 0.003,
      endFrequency: frequency * 1.018,
      filterFrequency: 2800,
      pan,
    });
    this.tone(frequency * 2.01, {
      duration: 0.095,
      gain: 0.018,
      type: "triangle",
      attack: 0.002,
      filterFrequency: 4200,
      pan,
    });
  }

  public shard(chain = 0, pan = 0): void {
    const step = Math.max(0, Math.floor(chain)) % 5;
    const frequency = 930 * 2 ** ((step * 2) / 12);
    this.tone(frequency, {
      duration: 0.13,
      gain: 0.045,
      type: "sine",
      attack: 0.002,
      endFrequency: frequency * 1.12,
      filterFrequency: 5200,
      pan,
    });
  }

  public receiverHit(
    quality: ReceiverHitQuality = "safe",
    combo = 0,
    pan = 0,
  ): void {
    const amount = qualityAmount(quality);
    const comboLift = Math.min(7, Math.max(0, Math.floor(combo))) * 0.018;
    const root = 245 * (1 + comboLift);
    const intervals = amount > 0.84 ? [1, 1.25, 1.5] : amount > 0.5 ? [1, 1.25] : [1];

    intervals.forEach((interval, index) => {
      this.tone(root * interval, {
        duration: 0.27 + amount * 0.16,
        gain: 0.042 + amount * 0.018,
        type: index === 0 ? "triangle" : "sine",
        attack: 0.006,
        delay: index * 0.017,
        endFrequency: root * interval * (1.015 + amount * 0.018),
        filterFrequency: 2400 + amount * 1600,
        pan: pan * (1 - index * 0.25),
      });
    });

    this.noise(0.09 + amount * 0.045, 0.018 + amount * 0.014, 2300, "bandpass", pan);
  }

  public overdriveStart(): void {
    if (this.overdriveActive) return;
    this.overdriveActive = true;
    this.applyAmbienceState(0.14);

    [196, 247, 330].forEach((frequency, index) => {
      this.tone(frequency, {
        duration: 0.48,
        gain: 0.032,
        type: index === 0 ? "triangle" : "sine",
        attack: 0.018,
        delay: index * 0.028,
        endFrequency: frequency * 1.5,
        filterFrequency: 2200,
        pan: (index - 1) * 0.38,
      });
    });
  }

  public overdriveEnd(playCue = true): void {
    if (!this.overdriveActive) return;
    this.overdriveActive = false;
    this.applyAmbienceState(0.2);

    if (playCue) {
      this.tone(440, {
        duration: 0.2,
        gain: 0.032,
        type: "sine",
        attack: 0.006,
        endFrequency: 330,
        filterFrequency: 1900,
      });
    }
  }

  public failure(): void {
    this.focusEnd(true);
    this.overdriveEnd(false);
    this.tone(190, {
      duration: 0.52,
      gain: 0.075,
      type: "sawtooth",
      attack: 0.008,
      endFrequency: 58,
      filterFrequency: 720,
    });
    this.noise(0.34, 0.042, 520, "lowpass", 0);
  }

  /** Fades and suspends the context without tearing down the ambience graph. */
  public pause(): void {
    this.pausedByUser = true;
    this.focusEnd(true);
    this.suspendContextSoon();
  }

  /** Resume should normally be called by the pause UI's pointer/click handler. */
  public async resume(): Promise<boolean> {
    this.pausedByUser = false;
    if (this.destroyed || this.pausedByVisibility || !this.context) return false;
    return this.resumeContext(this.context);
  }

  /** Public for hosts that centralize document visibility handling. */
  public handleVisibilityChange(hidden = this.documentIsHidden()): void {
    this.pausedByVisibility = hidden;
    if (hidden) {
      this.focusEnd(true);
      this.suspendContextSoon();
    }
    // On return, stay silent until resume() or the next trusted gesture. This
    // avoids surprise audio after lock-screen, Control Center, or a phone call.
  }

  public destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.detachLifecycleListeners();

    if (this.pauseTimer !== undefined) {
      globalThis.clearTimeout(this.pauseTimer);
      this.pauseTimer = undefined;
    }

    this.focusEnd(true);
    const context = this.context;
    if (!context) return;

    const now = context.currentTime;
    this.fadeToSilence(this.masterBus?.gain, now, 0.025);
    this.stopAllVoices(now);
    this.stopAmbience(now + 0.035);

    globalThis.setTimeout(() => {
      if (context.state !== "closed") void context.close().catch(() => undefined);
    }, 55);
  }

  private readonly handleUnlockGesture = (): void => {
    if (this.pausedByUser || this.pausedByVisibility) return;
    void this.unlock();
  };

  private readonly handleDocumentVisibility = (): void => {
    this.handleVisibilityChange();
  };

  private readonly handlePageHide = (): void => {
    this.handleVisibilityChange(true);
  };

  private readonly handlePageShow = (): void => {
    this.handleVisibilityChange(this.documentIsHidden());
  };

  private attachLifecycleListeners(): void {
    if (typeof window === "undefined" || typeof document === "undefined") return;

    window.addEventListener("pointerdown", this.handleUnlockGesture, {
      capture: true,
      passive: true,
    });
    window.addEventListener("touchend", this.handleUnlockGesture, {
      capture: true,
      passive: true,
    });
    window.addEventListener("keydown", this.handleUnlockGesture, {
      capture: true,
      passive: true,
    });
    document.addEventListener("visibilitychange", this.handleDocumentVisibility);
    window.addEventListener("pagehide", this.handlePageHide);
    window.addEventListener("pageshow", this.handlePageShow);
  }

  private detachLifecycleListeners(): void {
    if (typeof window === "undefined" || typeof document === "undefined") return;

    window.removeEventListener("pointerdown", this.handleUnlockGesture, true);
    window.removeEventListener("touchend", this.handleUnlockGesture, true);
    window.removeEventListener("keydown", this.handleUnlockGesture, true);
    document.removeEventListener("visibilitychange", this.handleDocumentVisibility);
    window.removeEventListener("pagehide", this.handlePageHide);
    window.removeEventListener("pageshow", this.handlePageShow);
  }

  private createContext(): AudioContext | undefined {
    const Context = getAudioContextConstructor();
    if (!Context) {
      this.contextUnavailable = true;
      return undefined;
    }

    try {
      let context: AudioContext;
      try {
        context = new Context({ latencyHint: "interactive" });
      } catch {
        context = new Context();
      }

      const master = context.createGain();
      const music = context.createGain();
      const sfx = context.createGain();
      const limiter = context.createDynamicsCompressor();

      limiter.threshold.setValueAtTime(-6, context.currentTime);
      limiter.knee.setValueAtTime(1, context.currentTime);
      limiter.ratio.setValueAtTime(20, context.currentTime);
      limiter.attack.setValueAtTime(0.003, context.currentTime);
      limiter.release.setValueAtTime(0.16, context.currentTime);

      music.connect(master);
      sfx.connect(master);
      master.connect(limiter);
      limiter.connect(context.destination);

      this.context = context;
      this.masterBus = master;
      this.musicBus = music;
      this.sfxBus = sfx;
      this.noiseBuffer = this.createNoiseBuffer(context);
      this.startAmbience(context, music);
      this.applyBusLevels(0.01);
      return context;
    } catch {
      this.contextUnavailable = true;
      this.context = undefined;
      return undefined;
    }
  }

  private async resumeContext(context: AudioContext): Promise<boolean> {
    if (this.destroyed || this.pausedByUser || this.pausedByVisibility) return false;

    if (this.pauseTimer !== undefined) {
      globalThis.clearTimeout(this.pauseTimer);
      this.pauseTimer = undefined;
    }

    try {
      if (context.state !== "running") await context.resume();
      this.applyBusLevels(0.055);
      this.applyAmbienceState(0.09);
      return context.state === "running";
    } catch {
      // Safari may require one more direct gesture after an interruption.
      return false;
    }
  }

  private suspendContextSoon(): void {
    const context = this.context;
    if (!context || context.state === "closed") return;

    if (this.pauseTimer !== undefined) globalThis.clearTimeout(this.pauseTimer);
    this.fadeToSilence(this.masterBus?.gain, context.currentTime, 0.04);

    this.pauseTimer = globalThis.setTimeout(() => {
      this.pauseTimer = undefined;
      if (context.state === "running") void context.suspend().catch(() => undefined);
    }, 58);
  }

  private startAmbience(context: AudioContext, destination: AudioNode): void {
    if (this.ambience || !this.noiseBuffer) return;

    const now = context.currentTime;
    const droneA = context.createOscillator();
    const droneB = context.createOscillator();
    const air = context.createBufferSource();
    const airFilter = context.createBiquadFilter();
    const airGain = context.createGain();
    const filter = context.createBiquadFilter();
    const gain = context.createGain();
    const pulse = context.createOscillator();
    const pulseDepth = context.createGain();

    droneA.type = "sine";
    droneA.frequency.setValueAtTime(55, now);
    droneB.type = "triangle";
    droneB.frequency.setValueAtTime(82.41, now);
    droneB.detune.setValueAtTime(-7, now);

    air.buffer = this.noiseBuffer;
    air.loop = true;
    airFilter.type = "bandpass";
    airFilter.frequency.setValueAtTime(1050, now);
    airFilter.Q.setValueAtTime(0.55, now);
    airGain.gain.setValueAtTime(0.013, now);

    filter.type = "lowpass";
    filter.frequency.setValueAtTime(680, now);
    filter.Q.setValueAtTime(0.8, now);
    gain.gain.setValueAtTime(MIN_GAIN, now);
    gain.gain.exponentialRampToValueAtTime(0.028, now + 0.7);

    pulse.type = "sine";
    pulse.frequency.setValueAtTime(0.42, now);
    pulseDepth.gain.setValueAtTime(0.0075, now);

    droneA.connect(filter);
    droneB.connect(filter);
    filter.connect(gain);
    air.connect(airFilter);
    airFilter.connect(airGain);
    airGain.connect(gain);
    pulse.connect(pulseDepth);
    pulseDepth.connect(gain.gain);
    gain.connect(destination);

    droneA.start(now);
    droneB.start(now);
    air.start(now, 0.173);
    pulse.start(now);

    this.ambience = { droneA, droneB, air, filter, gain, pulse, pulseDepth };
  }

  private applyAmbienceState(seconds: number): void {
    if (!this.context || !this.ambience) return;

    const now = this.context.currentTime;
    const active = this.overdriveActive;
    this.ramp(this.ambience.filter.frequency, active ? 1420 : 680, now, seconds);
    this.ramp(this.ambience.gain.gain, active ? 0.043 : 0.028, now, seconds);
    this.ramp(this.ambience.pulse.frequency, active ? 0.7 : 0.42, now, seconds);
    this.ramp(this.ambience.pulseDepth.gain, active ? 0.011 : 0.0075, now, seconds);
    this.ramp(this.ambience.droneB.detune, active ? 486 : -7, now, seconds * 1.2);
  }

  private stopAmbience(when: number): void {
    const ambience = this.ambience;
    this.ambience = undefined;
    if (!ambience) return;

    [ambience.droneA, ambience.droneB, ambience.air, ambience.pulse].forEach((source) => {
      try {
        source.stop(when);
      } catch {
        // Safe during teardown after an interrupted/closed context.
      }
    });
  }

  private tone(frequency: number, options: ToneOptions): void {
    const graph = this.getSfxGraph();
    if (!graph) return;

    this.ensureVoiceCapacity(1);
    const { context, bus } = graph;
    const start = context.currentTime + Math.max(0, options.delay ?? 0);
    const duration = Math.max(0.035, options.duration);
    const attack = Math.min(duration * 0.4, Math.max(0.002, options.attack ?? 0.005));
    const end = start + duration;
    const oscillator = context.createOscillator();
    const filter = context.createBiquadFilter();
    const gain = context.createGain();
    const panner = context.createStereoPanner();

    oscillator.type = options.type ?? "sine";
    oscillator.frequency.setValueAtTime(Math.max(20, frequency), start);
    if (options.endFrequency !== undefined) {
      oscillator.frequency.exponentialRampToValueAtTime(
        Math.max(20, options.endFrequency),
        end,
      );
    }

    filter.type = "lowpass";
    filter.Q.setValueAtTime(0.7, start);
    filter.frequency.setValueAtTime(options.filterFrequency ?? 3200, start);
    panner.pan.setValueAtTime(clamp(options.pan ?? 0, -1, 1), start);

    gain.gain.setValueAtTime(MIN_GAIN, start);
    gain.gain.exponentialRampToValueAtTime(Math.max(MIN_GAIN, options.gain), start + attack);
    gain.gain.exponentialRampToValueAtTime(MIN_GAIN, end);

    oscillator.connect(filter);
    filter.connect(gain);
    gain.connect(panner);
    panner.connect(bus);
    oscillator.start(start);
    oscillator.stop(end + 0.018);
    this.trackVoice([oscillator], gain, start, end + 0.018);
  }

  private noise(
    duration: number,
    level: number,
    frequency: number,
    filterType: BiquadFilterType,
    pan: number,
  ): void {
    const graph = this.getSfxGraph();
    if (!graph || !this.noiseBuffer) return;

    this.ensureVoiceCapacity(1);
    const { context, bus } = graph;
    const now = context.currentTime;
    const end = now + Math.max(0.04, duration);
    const source = context.createBufferSource();
    const filter = context.createBiquadFilter();
    const gain = context.createGain();
    const panner = context.createStereoPanner();

    source.buffer = this.noiseBuffer;
    filter.type = filterType;
    filter.frequency.setValueAtTime(frequency, now);
    filter.Q.setValueAtTime(filterType === "bandpass" ? 1.2 : 0.7, now);
    panner.pan.setValueAtTime(clamp(pan, -1, 1), now);
    gain.gain.setValueAtTime(MIN_GAIN, now);
    gain.gain.exponentialRampToValueAtTime(Math.max(MIN_GAIN, level), now + 0.004);
    gain.gain.exponentialRampToValueAtTime(MIN_GAIN, end);

    source.connect(filter);
    filter.connect(gain);
    gain.connect(panner);
    panner.connect(bus);
    source.start(now, (now * 0.731) % 1.25, duration + 0.018);
    source.stop(end + 0.018);
    this.trackVoice([source], gain, now, end + 0.018);
  }

  private getSfxGraph(): { context: AudioContext; bus: GainNode } | undefined {
    if (
      this.destroyed ||
      this.mutedValue ||
      this.pausedByUser ||
      this.pausedByVisibility ||
      !this.context ||
      !this.sfxBus
    ) {
      return undefined;
    }

    return { context: this.context, bus: this.sfxBus };
  }

  private trackVoice(
    sources: SourceNode[],
    gain: GainNode,
    startedAt: number,
    endsAt: number,
  ): void {
    const voice: Voice = { sources, gain, startedAt, endsAt, stopped: false };
    this.voices.push(voice);
    let sourcesEnded = 0;

    sources.forEach((source) => {
      source.addEventListener(
        "ended",
        () => {
          sourcesEnded += 1;
          if (sourcesEnded === sources.length) this.removeVoice(voice);
        },
        { once: true },
      );
    });
  }

  private ensureVoiceCapacity(incoming: number): void {
    if (!this.context) return;

    const now = this.context.currentTime;
    for (let index = this.voices.length - 1; index >= 0; index -= 1) {
      const voice = this.voices[index];
      if (voice && voice.endsAt < now - 0.05) this.voices.splice(index, 1);
    }

    const focusCount = this.focus && !this.focus.stopped ? 1 : 0;
    while (this.voices.length + focusCount + incoming > MAX_SFX_VOICES) {
      let oldestIndex = 0;
      for (let index = 1; index < this.voices.length; index += 1) {
        const candidate = this.voices[index];
        const oldest = this.voices[oldestIndex];
        if (candidate && oldest && candidate.startedAt < oldest.startedAt) oldestIndex = index;
      }

      const [oldest] = this.voices.splice(oldestIndex, 1);
      if (!oldest) break;
      this.stopVoice(oldest, now, 0.014);
    }
  }

  private stopVoice(voice: Voice, now: number, release: number): void {
    if (voice.stopped) return;
    voice.stopped = true;
    this.fadeToSilence(voice.gain.gain, now, release);
    voice.sources.forEach((source) => {
      try {
        source.stop(now + release + 0.008);
      } catch {
        // stop() throws if a source has already completed.
      }
    });
  }

  private stopAllVoices(now: number): void {
    const voices = this.voices.splice(0);
    voices.forEach((voice) => this.stopVoice(voice, now, 0.018));
  }

  private removeVoice(voice: Voice): void {
    const index = this.voices.indexOf(voice);
    if (index >= 0) this.voices.splice(index, 1);
    voice.sources.forEach((source) => source.disconnect());
    voice.gain.disconnect();
  }

  private applyBusLevels(seconds: number): void {
    if (!this.context) return;

    const now = this.context.currentTime;
    const masterTarget = this.mutedValue ? MIN_GAIN : Math.max(MIN_GAIN, this.masterVolume);
    this.ramp(this.masterBus?.gain, masterTarget, now, seconds);
    this.ramp(this.musicBus?.gain, Math.max(MIN_GAIN, this.musicVolume), now, seconds);
    this.ramp(this.sfxBus?.gain, Math.max(MIN_GAIN, this.sfxVolume), now, seconds);
  }

  private ramp(
    parameter: AudioParam | undefined,
    target: number,
    now: number,
    seconds: number,
  ): void {
    if (!parameter) return;
    parameter.cancelScheduledValues(now);
    parameter.setValueAtTime(parameter.value, now);
    parameter.linearRampToValueAtTime(target, now + Math.max(0.004, seconds));
  }

  private setTarget(
    parameter: AudioParam,
    target: number,
    now: number,
    timeConstant: number,
  ): void {
    parameter.cancelScheduledValues(now);
    parameter.setValueAtTime(parameter.value, now);
    parameter.setTargetAtTime(target, now, timeConstant);
  }

  private fadeToSilence(
    parameter: AudioParam | undefined,
    now: number,
    seconds: number,
  ): void {
    if (!parameter) return;
    parameter.cancelScheduledValues(now);
    parameter.setValueAtTime(Math.max(MIN_GAIN, parameter.value), now);
    parameter.exponentialRampToValueAtTime(MIN_GAIN, now + Math.max(0.004, seconds));
  }

  private createNoiseBuffer(context: AudioContext): AudioBuffer {
    const length = Math.max(1, Math.floor(context.sampleRate * 2));
    const buffer = context.createBuffer(1, length, context.sampleRate);
    const data = buffer.getChannelData(0);
    let seed = 0x91e10da5;

    for (let index = 0; index < data.length; index += 1) {
      seed ^= seed << 13;
      seed ^= seed >>> 17;
      seed ^= seed << 5;
      data[index] = ((seed >>> 0) / 0xffffffff) * 2 - 1;
    }

    return buffer;
  }

  private readPersistedMutedState(): boolean | undefined {
    if (typeof localStorage === "undefined") return undefined;
    try {
      const value = localStorage.getItem(this.storageKey);
      if (value === "true") return true;
      if (value === "false") return false;
    } catch {
      // Storage can be unavailable in private browsing or embedded contexts.
    }
    return undefined;
  }

  private persistMutedState(muted: boolean): void {
    if (typeof localStorage === "undefined") return;
    try {
      localStorage.setItem(this.storageKey, String(muted));
    } catch {
      // Muting still works for the current session when storage is unavailable.
    }
  }

  private documentIsHidden(): boolean {
    return typeof document !== "undefined" && document.hidden;
  }
}

export function createGameAudio(options?: GameAudioOptions): GameAudio {
  return new GameAudio(options);
}
